import React, { useState, useEffect } from 'react';
import DokterLayout from '../DokterLayout';
import AdminCrudModal from '../../../components/AdminCrudModal';
import axiosInstance from '../../../api/axios';
import { normalizeErrorMessage, normalizeFieldErrors } from '../../../services/adminCrudApi';
import { validateJadwalForm } from '../../../services/adminMasterValidation';
import './JadwalDokter.css';

const HARI_LIST = ['Senin', 'Selasa', 'Rabu', 'Kamis', 'Jumat', 'Sabtu', 'Minggu'];

// Helper untuk mendapatkan waktu WIB (UTC+7) secara manual
const getWIBDate = (date = new Date()) => {
  const utcTimestamp = date.getTime() + date.getTimezoneOffset() * 60000;
  const wibTimestamp = utcTimestamp + (7 * 60 * 60 * 1000);
  return new Date(wibTimestamp);
};

const getHariIni = () => {
  const days = ['Minggu', 'Senin', 'Selasa', 'Rabu', 'Kamis', 'Jumat', 'Sabtu'];
  const wibDate = getWIBDate();
  return days[wibDate.getUTCDay()];
};

const EMPTY_FORM = {
  hari: '',
  jam_mulai: '',
  jam_selesai: '',
  kapasitas: 10,
};

export default function JadwalDokter() {
  const [jadwal, setJadwal] = useState([]);
  const [dokter, setDokter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState('');
  const [submitLoading, setSubmitLoading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [notice, setNotice] = useState('');
  const [istirahatLoading, setIstirahatLoading] = useState(false);
  const [checkLoading, setCheckLoading] = useState(null);

  useEffect(() => {
    fetchJadwal();
  }, []);

  const fetchJadwal = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get('/dokter/jadwal');
      setJadwal(res.data?.jadwal ?? []);
      setDokter(res.data?.dokter ?? null);
    } catch (err) {
      console.error('Error fetching jadwal:', err);
      setJadwal([]);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setErrors((prev) => ({ ...prev, [name]: null }));
  };

  const openCreateForm = () => {
    setEditingId(null);
    setFormData(EMPTY_FORM);
    setErrors({});
    setSubmitError('');
    setShowForm(true);
  };

  const openEditForm = (item) => {
    setEditingId(item.id);
    setFormData({
      hari: item.hari,
      jam_mulai: item.jam_mulai?.substring(0, 5) || '',
      jam_selesai: item.jam_selesai?.substring(0, 5) || '',
      kapasitas: item.kapasitas ?? 10,
    });
    setErrors({});
    setSubmitError('');
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingId(null);
    setFormData(EMPTY_FORM);
    setErrors({});
    setSubmitError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const clientErrors = validateJadwalForm(formData, { skipDokter: true });
    if (Object.keys(clientErrors).length > 0) {
      setErrors(clientErrors);
      return;
    }

    const payload = {
      hari: formData.hari,
      jam_mulai: formData.jam_mulai,
      jam_selesai: formData.jam_selesai,
      kapasitas: Number(formData.kapasitas),
    };

    setSubmitLoading(true);
    setSubmitError('');

    try {
      if (editingId) {
        await axiosInstance.put(`/dokter/jadwal/${editingId}`, payload);
        setNotice('Jadwal berhasil diperbarui');
      } else {
        await axiosInstance.post('/dokter/jadwal', payload);
        setNotice('Jadwal berhasil ditambahkan');
      }
      closeForm();
      fetchJadwal();
    } catch (err) {
      setErrors(normalizeFieldErrors(err));
      setSubmitError(normalizeErrorMessage(err));
    } finally {
      setSubmitLoading(false);
    }
  };

  const toggleIstirahat = async () => {
    if (!dokter) return;

    const mulaiIstirahat = !dokter.sedang_istirahat;
    setIstirahatLoading(true);
    setSubmitError('');

    try {
      const res = await axiosInstance.put('/dokter/istirahat', {
        sedang_istirahat: mulaiIstirahat,
      });
      setDokter(res.data?.dokter ?? { ...dokter, sedang_istirahat: mulaiIstirahat });
      setNotice(res.data?.message || (mulaiIstirahat ? 'Mode istirahat diaktifkan' : 'Anda kembali praktik'));
    } catch (err) {
      setSubmitError(normalizeErrorMessage(err, 'Gagal mengubah status istirahat'));
    } finally {
      setIstirahatLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;

    setDeleteLoading(true);
    try {
      await axiosInstance.delete(`/dokter/jadwal/${deleteTarget.id}`);
      setNotice('Jadwal berhasil dihapus');
      setDeleteTarget(null);
      fetchJadwal();
    } catch (err) {
      setSubmitError(normalizeErrorMessage(err, 'Gagal menghapus jadwal'));
    } finally {
      setDeleteLoading(false);
    }
  };

  const toggleLibur = async (item) => {
    setSubmitError('');
    try {
      await axiosInstance.put(`/dokter/jadwal/${item.id}`, { status: !item.status });
      setNotice(`Jadwal hari ${item.hari} berhasil diatur menjadi ${!item.status ? 'Aktif' : 'Libur'}`);
      fetchJadwal();
    } catch (err) {
      setSubmitError(normalizeErrorMessage(err, 'Gagal mengubah status jadwal'));
    }
  };

  const handleCheckTime = async (item, field) => {
    const tipe = field === 'jam_mulai' ? 'check_in' : 'check_out';

    if (item.hari !== getHariIni()) {
      setNotice(`Check ${tipe === 'check_in' ? 'in' : 'out'} hanya bisa dilakukan pada hari ${item.hari}. Hari ini: ${getHariIni()}.`);
      return;
    }

    if (tipe === 'check_in' && item.sudah_check_in) {
      setNotice('Check in hari ini sudah dicatat.');
      return;
    }

    if (tipe === 'check_out' && item.sudah_check_out) {
      setNotice('Check out hari ini sudah dicatat.');
      return;
    }

    if (!item.status || dokter?.sedang_istirahat) {
      setNotice('Jadwal tidak aktif atau sedang istirahat.');
      return;
    }

    setCheckLoading(`${item.id}-${field}`);
    setSubmitError('');

    try {
      const res = await axiosInstance.post(`/dokter/jadwal/${item.id}/absensi`, { tipe });
      setNotice(res.data?.message || 'Waktu berhasil dicatat');
      fetchJadwal();
    } catch (err) {
      setSubmitError(normalizeErrorMessage(err, 'Gagal mencatat waktu'));
    } finally {
      setCheckLoading(null);
    }
  };

  return (
    <DokterLayout title="Jadwal Praktik">
      <div className="jadwal-container">
        {dokter && (
          <div className="jadwal-header-simple">
            <div>
              <h2>{dokter.nama}</h2>
              <p>{dokter.spesialisasi} • {dokter.email}</p>
            </div>
            <div className="jadwal-header-actions">
              <button
                type="button"
                className={`jadwal-btn ${dokter.sedang_istirahat ? 'jadwal-btn--success' : 'jadwal-btn--warning'}`}
                onClick={toggleIstirahat}
                disabled={istirahatLoading}
              >
                {istirahatLoading
                  ? 'Memproses...'
                  : dokter.sedang_istirahat
                    ? 'Kembali Praktik'
                    : 'Mulai Istirahat'}
              </button>
              <button type="button" className="jadwal-btn jadwal-btn--primary" onClick={openCreateForm}>
                + Tambah Jadwal
              </button>
            </div>
          </div>
        )}

        {dokter?.sedang_istirahat && (
          <div className="jadwal-istirahat-banner">
            Anda sedang istirahat. Pasien tidak dapat mendaftar hingga Anda menekan <strong>Kembali Praktik</strong>.
          </div>
        )}

        {notice && (
          <p className="jadwal-notice">{notice}</p>
        )}

        {submitError && !showForm && !deleteTarget && (
          <p className="jadwal-form-error">{submitError}</p>
        )}

        <div className="jadwal-grid-minimal">
          {loading ? (
            <div className="state-msg">Memuat jadwal...</div>
          ) : jadwal.length === 0 ? (
            <div className="state-msg">
              Belum ada jadwal. Klik &quot;Tambah Jadwal&quot; untuk mengatur jadwal praktik Anda.
            </div>
          ) : (
            jadwal.map((item) => (
              <div
                key={item.id}
                className={`jadwal-card-clean ${!item.status || dokter?.sedang_istirahat ? 'is-off' : ''}`}
              >
                <div className="card-top">
                  <h4>{item.hari}</h4>
                  {dokter?.sedang_istirahat ? (
                    <span className="jadwal-badge-off jadwal-badge-break">Istirahat</span>
                  ) : !item.status ? (
                    <span className="jadwal-badge-off">Nonaktif</span>
                  ) : null}
                </div>

                <div className="card-bottom">
                  <span className="time-label">Check In</span>
                  <button
                    type="button"
                    className={`time-value time-value-btn${item.sudah_check_in ? ' time-value-btn--done' : ''}`}
                    onClick={() => handleCheckTime(item, 'jam_mulai')}
                    disabled={
                      Boolean(checkLoading)
                      || !item.status
                      || dokter?.sedang_istirahat
                      || item.sudah_check_in
                      || item.hari !== getHariIni()
                    }
                    title={item.sudah_check_in ? 'Check in hari ini sudah dicatat' : 'Klik untuk catat check in'}
                  >
                    {checkLoading === `${item.id}-jam_mulai`
                      ? 'Memproses...'
                      : item.jam_check_in_hari_ini || '—'}
                  </button>
                  <span className="time-label">Check Out</span>
                  <button
                    type="button"
                    className={`time-value time-value-btn${item.sudah_check_out ? ' time-value-btn--done' : ''}`}
                    onClick={() => handleCheckTime(item, 'jam_selesai')}
                    disabled={
                      Boolean(checkLoading)
                      || !item.status
                      || dokter?.sedang_istirahat
                      || item.sudah_check_out
                      || item.hari !== getHariIni()
                    }
                    title={item.sudah_check_out ? 'Check out hari ini sudah dicatat' : 'Klik untuk catat check out'}
                  >
                    {checkLoading === `${item.id}-jam_selesai`
                      ? 'Memproses...'
                      : item.jam_check_out_hari_ini || '—'}
                  </button>
                  <span className="time-label">Kapasitas</span>
                  <p className="time-value">{item.kapasitas} pasien</p>
                  <span className="time-label">Pasien Diperiksa</span>
                  <p className="time-value">{item.pasien_diperiksa ?? 0} pasien</p>
                </div>

                <div className="jadwal-card-actions">
                  <button 
                    type="button" 
                    className={`jadwal-btn ${item.status ? 'jadwal-btn--warning' : 'jadwal-btn--success'}`} 
                    onClick={() => toggleLibur(item)}
                  >
                    {item.status ? 'Libur' : 'Aktifkan'}
                  </button>
                  <button type="button" className="jadwal-btn" onClick={() => openEditForm(item)}>
                    Edit
                  </button>
                  <button
                    type="button"
                    className="jadwal-btn jadwal-btn--danger"
                    onClick={() => setDeleteTarget(item)}
                  >
                    Hapus
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      <AdminCrudModal
        open={showForm}
        onClose={closeForm}
        title={editingId ? 'Edit Jadwal Praktik' : 'Tambah Jadwal Praktik'}
      >
        {submitError && <p className="jadwal-form-error">{submitError}</p>}

        <form className="jadwal-form" onSubmit={handleSubmit}>
          <div className="jadwal-form-group">
            <label>Hari <span>*</span></label>
            <div className="jadwal-day-grid">
              {HARI_LIST.map((day) => (
                <button
                  key={day}
                  type="button"
                  className={`jadwal-day-btn ${formData.hari === day ? 'is-active' : ''}`}
                  onClick={() => {
                    setFormData((prev) => ({ ...prev, hari: day }));
                    setErrors((prev) => ({ ...prev, hari: null }));
                  }}
                >
                  {day.substring(0, 3)}
                </button>
              ))}
            </div>
            {errors.hari && <small className="field-error">{errors.hari}</small>}
          </div>

          <div className="jadwal-form-row">
            <div className="jadwal-form-group">
              <label htmlFor="jam_mulai">Jam Check In <span>*</span></label>
              <input
                id="jam_mulai"
                type="time"
                name="jam_mulai"
                value={formData.jam_mulai}
                onChange={handleChange}
                required
              />
              {errors.jam_mulai && <small className="field-error">{errors.jam_mulai}</small>}
            </div>
            <div className="jadwal-form-group">
              <label htmlFor="jam_selesai">Jam Check Out <span>*</span></label>
              <input
                id="jam_selesai"
                type="time"
                name="jam_selesai"
                value={formData.jam_selesai}
                onChange={handleChange}
                required
              />
              {errors.jam_selesai && <small className="field-error">{errors.jam_selesai}</small>}
            </div>
          </div>

          <div className="jadwal-form-group">
            <label htmlFor="kapasitas">Kapasitas pasien <span>*</span></label>
            <input
              id="kapasitas"
              type="number"
              name="kapasitas"
              min="1"
              max="500"
              value={formData.kapasitas}
              onChange={handleChange}
              required
            />
            {errors.kapasitas && <small className="field-error">{errors.kapasitas}</small>}
          </div>

          <div className="jadwal-form-actions">
            <button type="button" className="jadwal-btn" onClick={closeForm}>
              Batal
            </button>
            <button type="submit" className="jadwal-btn jadwal-btn--primary" disabled={submitLoading}>
              {submitLoading ? 'Menyimpan...' : editingId ? 'Simpan Perubahan' : 'Tambah Jadwal'}
            </button>
          </div>
        </form>
      </AdminCrudModal>

      <AdminCrudModal
        open={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        title="Hapus Jadwal"
      >
        <p className="jadwal-delete-text">
          Hapus jadwal <strong>{deleteTarget?.hari}</strong> ({deleteTarget?.jam_mulai?.slice(0, 5)} - {deleteTarget?.jam_selesai?.slice(0, 5)})?
        </p>
        <div className="jadwal-form-actions">
          <button type="button" className="jadwal-btn" onClick={() => setDeleteTarget(null)}>
            Batal
          </button>
          <button
            type="button"
            className="jadwal-btn jadwal-btn--danger"
            onClick={handleDelete}
            disabled={deleteLoading}
          >
            {deleteLoading ? 'Menghapus...' : 'Hapus'}
          </button>
        </div>
      </AdminCrudModal>
    </DokterLayout>
  );
}
